import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationHeader from '../../components/ui/NavigationHeader';
import DashboardStats from './components/DashboardStats';
import DashboardFilters from './components/DashboardFilters';
import ClientGrid from './components/ClientGrid';
// import QuickActions from './components/QuickActions';
import { clientApiService } from '../../utils/clientApiService';
import { transactionApiService } from '../../utils/transactionApiService';

const ClientDashboard = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [searchQuery, setSearchQuery] = useState('');

  // Load clients data
  useEffect(() => {
    const loadClients = async () => {
      setIsLoading(true);
      try {
        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Load clients using the centralized service
        const loadedClients = await clientApiService.getAllClients();
        setClients(loadedClients);
      } catch (error) {
        console.error('Error loading clients:', error);
        setClients([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadClients();
  }, []);


  // Helper to get current username from JWT
  const getCurrentUsername = () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return 'Unknown';
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.username || 'Unknown';
    } catch {
      return 'Unknown';
    }
  };

  const isAdmin = () => {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) return false;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.role === 'admin';
  } catch {
    return false;
  }
};

  // Handle adding minutes to client (with transaction)
  const handleAddMinutes = async (clientId, minutes) => {
    try {
      const client = clients.find(c => c.id === clientId);
      if (!client) throw new Error('Client not found');
      await transactionApiService.addTransaction({
        clientId,
        type: 'add',
        amount: Number(minutes),
        description: `Quick add ${minutes} minutes`,
        staff: getCurrentUsername()
      });
      // Refresh client data
      const updatedClient = await clientApiService.getClientById(clientId);
      setClients(prevClients => prevClients.map(client => client.id === clientId ? updatedClient : client));
      console.log(`Added ${minutes} minutes to client ${clientId}`);
    } catch (error) {
      console.error('Error adding minutes:', error);
      alert(error.message || 'Failed to add minutes. Please try again.');
    }
  };

  // Handle subtracting minutes from client (with transaction)
  const handleSubtractMinutes = async (clientId, minutes) => {
    try {
      const client = clients.find(c => c.id === clientId);
      if (!client) throw new Error('Client not found');
      if (client.balance < minutes) throw new Error('Insufficient balance for this transaction');
      await transactionApiService.addTransaction({
        clientId,
        type: 'subtract',
        amount: Number(minutes),
        description: `Quick subtract ${minutes} minutes`,
        staff: getCurrentUsername()
      });
      // Refresh client data
      const updatedClient = await clientApiService.getClientById(clientId);
      setClients(prevClients => prevClients.map(client => client.id === clientId ? updatedClient : client));
      console.log(`Subtracted ${minutes} minutes from client ${clientId}`);
    } catch (error) {
      console.error('Error subtracting minutes:', error);
      alert(error.message || 'Failed to subtract minutes. Please try again.');
    }
  };

  // Handle refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      // Reload clients from storage using the service
      const refreshedClients = await clientApiService.getAllClients();
      setClients(refreshedClients);
    } catch (error) {
      console.error('Error refreshing clients:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Filter and sort clients
  const filteredAndSortedClients = useMemo(() => {
    let filtered = [...clients];

    // Apply search filter
    if (searchQuery.trim()) {
      const term = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(client =>
        (client.name || '').toLowerCase().includes(term) ||
        (client.phone || '').replace(/\D/g, '').includes(term.replace(/\D/g, ''))
      );
    }

    // Apply status filter
    switch (activeFilter) {
      case 'active':
        filtered = filtered.filter(client => client?.balance > 0);
        break;
      case 'low':
        filtered = filtered.filter(client => client?.balance > 0 && client?.balance <= 30);
        break;
      case 'empty':
        filtered = filtered.filter(client => client?.balance === 0);
        break;
      default:
        // 'all' - no additional filtering
        break;
    }

    // Apply sorting
    switch (sortBy) {
      case 'balance-high':
        filtered.sort((a, b) => (b?.balance || 0) - (a?.balance || 0));
        break;
      case 'balance-low':
        filtered.sort((a, b) => (a?.balance || 0) - (b?.balance || 0));
        break;
      case 'recent':
        filtered.sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0));
        break;
      default:
        // 'name' - alphabetical
        filtered.sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
        break;
    }

    return filtered;
  }, [clients, activeFilter, sortBy, searchQuery]);

  return (
    <div className="min-h-screen bg-background">
      <NavigationHeader />
      
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Client Dashboard
            </h1>
            <p className="text-muted-foreground">
              Manage your solarium clients and track their minute balances
            </p>
          </div>

          {/* Dashboard Stats */}
          {isAdmin() && <DashboardStats clients={clients} />}

          {/* Quick Actions
          <QuickActions 
            onRefresh={handleRefresh}
            isRefreshing={isRefreshing}
          /> */}

          {/* Filters */}
          <DashboardFilters
            onFilterChange={setActiveFilter}
            onSortChange={setSortBy}
            clientCount={filteredAndSortedClients.length}
          />

          {/* Client Grid */}
          <ClientGrid
            clients={filteredAndSortedClients}
            onAddMinutes={handleAddMinutes}
            onSubtractMinutes={handleSubtractMinutes}
            isLoading={isLoading}
          />
        </div>
      </main>
    </div>
  );
};

export default ClientDashboard;